import React, { useEffect, useMemo, useState } from 'react';
import { sendPasswordResetEmail } from 'firebase/auth';
import { Timestamp } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { Mail, Save, ShieldCheck, UserPlus } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { auth } from '../../services/firebase';
import { employeesService } from '../../services/hrService';
import { subscriptionService } from '../../services/subscriptionService';
import type { Employee } from '../../types/firestore';
import { getDefaultEmployeePermissions } from '../../utils/employeePermissions';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Modal from '../ui/Modal';
import StatusBadge from '../ui/StatusBadge';

interface CreateEmployeeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (employee: Employee) => void;
}

type DepartmentOption = {
  value: Employee['department'];
  label: string;
};

type RoleOption = {
  value: Employee['role'];
  label: string;
  position: string;
};

type PayrollType = Employee['salary']['payrollType'];
type ContractType = Employee['employment']['contractType'];

interface FormData {
  name: string;
  email: string;
  phone: string;
  employeeId: string;
  department: Employee['department'];
  role: Employee['role'];
  position: string;
  hireDate: string;
  contractType: ContractType;
  baseSalary: string;
  payrollType: PayrollType;
  nationalId: string;
  dateOfBirth: string;
  address: string;
  emergencyContactName: string;
  emergencyContactPhone: string;
  emergencyContactRelation: string;
}

const departments: DepartmentOption[] = [
  { value: 'management', label: 'الإدارة' },
  { value: 'teaching', label: 'التعليم' },
  { value: 'finance', label: 'المالية' },
  { value: 'hr', label: 'الموارد البشرية' },
  { value: 'customer_service', label: 'خدمة العملاء' },
  { value: 'technical', label: 'تقنية المعلومات' },
  { value: 'administration', label: 'الشؤون الإدارية' },
];

const roles: RoleOption[] = [
  { value: 'employee', label: 'موظف', position: 'موظف' },
  { value: 'assistant', label: 'مساعد إداري', position: 'مساعد إداري' },
  { value: 'supervisor', label: 'مشرف', position: 'مشرف' },
  { value: 'accountant', label: 'محاسب', position: 'محاسب' },
  { value: 'customer_service', label: 'خدمة عملاء', position: 'موظف خدمة عملاء' },
  { value: 'teacher', label: 'مدرس', position: 'مدرس' },
  { value: 'hr_manager', label: 'مسؤول موارد بشرية', position: 'مسؤول موارد بشرية' },
  { value: 'admin', label: 'مدير', position: 'مدير' },
];

const today = () => new Date().toISOString().slice(0, 10);

const generateEmployeeId = () =>
  `EMP-${Math.floor(Math.random() * 100000000)
    .toString()
    .padStart(8, '0')}`;

const emptyForm = (): FormData => ({
  name: '',
  email: '',
  phone: '',
  employeeId: generateEmployeeId(),
  department: 'administration',
  role: 'employee',
  position: 'موظف',
  hireDate: today(),
  contractType: 'full_time',
  baseSalary: '',
  payrollType: 'monthly',
  nationalId: '',
  dateOfBirth: '',
  address: '',
  emergencyContactName: '',
  emergencyContactPhone: '',
  emergencyContactRelation: '',
});

const toTimestamp = (value: string, fallback: Date) => {
  if (!value) return Timestamp.fromDate(fallback);
  const parsed = new Date(value);
  return Timestamp.fromDate(Number.isNaN(parsed.getTime()) ? fallback : parsed);
};

const friendlyError = (error: unknown) => {
  const message = error instanceof Error ? error.message : '';
  const code =
    typeof error === 'object' && error && 'code' in error
      ? String((error as { code?: unknown }).code)
      : '';
  if (message.includes('already-exists') || message.includes('already exists')) {
    return 'البريد الإلكتروني أو رقم الموظف مستخدم بالفعل داخل هذه المؤسسة.';
  }
  if (message.includes('permission') || message.includes('unauthenticated')) {
    return 'لا تملك صلاحية إنشاء حساب موظف.';
  }
  if (code.includes('internal') || message.toLowerCase() === 'internal') {
    return 'حدث خطأ داخلي أثناء إنشاء حساب الموظف. حاول مرة أخرى بعد تحديث الصفحة.';
  }
  return message || 'تعذر إنشاء الموظف. راجع البيانات وحاول مرة أخرى.';
};

const CreateEmployeeModal: React.FC<CreateEmployeeModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState<FormData>(() => emptyForm());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFormData(emptyForm());
      setErrors({});
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const permissions = useMemo(
    () => getDefaultEmployeePermissions(formData.role, formData.department),
    [formData.department, formData.role]
  );

  const setField = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    setFormData(current => ({ ...current, [field]: value }));
    setErrors(current => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const validate = () => {
    const nextErrors: Record<string, string> = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const baseSalary = Number(formData.baseSalary);

    if (!formData.name.trim()) nextErrors.name = 'اسم الموظف مطلوب.';
    if (!emailRegex.test(formData.email.trim())) {
      nextErrors.email = 'أدخل بريدًا إلكترونيًا صحيحًا.';
    }
    if (!formData.employeeId.trim()) {
      nextErrors.employeeId = 'رقم الموظف مطلوب.';
    }
    if (!formData.position.trim()) nextErrors.position = 'المسمى الوظيفي مطلوب.';
    if (!formData.hireDate) nextErrors.hireDate = 'تاريخ التعيين مطلوب.';
    if (!formData.baseSalary || Number.isNaN(baseSalary) || baseSalary < 0) {
      nextErrors.baseSalary = 'أدخل راتبًا صحيحًا لا يقل عن صفر.';
    }
    if (formData.phone && formData.phone.trim().length < 7) {
      nextErrors.phone = 'رقم الهاتف قصير جدًا.';
    }
    if (formData.emergencyContactPhone && formData.emergencyContactPhone.trim().length < 7) {
      nextErrors.emergencyContactPhone = 'رقم هاتف جهة الاتصال قصير جدًا.';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validate()) return;
    if (!user?.id) {
      setErrors({ general: 'يجب تسجيل الدخول أولًا.' });
      return;
    }

    setIsSubmitting(true);
    try {
      const limitCheck = await subscriptionService.canAddEmployee(user.id);
      if (!limitCheck.allowed) {
        setErrors({ general: limitCheck.messageAr });
        return;
      }

      const employeeData = {
        email: formData.email.trim(),
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        employeeId: formData.employeeId.trim(),
        role: formData.role,
        department: formData.department,
        position: formData.position.trim(),
        salary: {
          base: Number(formData.baseSalary),
          currency: 'EGP' as const,
          payrollType: formData.payrollType,
          overtimeRate: 0,
          allowances: {
            transport: 0,
            housing: 0,
            food: 0,
            other: 0,
          },
        },
        personalInfo: {
          dateOfBirth: toTimestamp(formData.dateOfBirth, new Date(0)),
          nationalId: formData.nationalId.trim(),
          address: formData.address.trim(),
          emergencyContact: {
            name: formData.emergencyContactName.trim(),
            phone: formData.emergencyContactPhone.trim(),
            relation: formData.emergencyContactRelation.trim(),
          },
          bankAccount: {
            accountNumber: '',
            bankName: '',
            iban: '',
          },
        },
        employment: {
          hireDate: toTimestamp(formData.hireDate, new Date()),
          contractType: formData.contractType,
        },
        permissions,
        accountStatus: 'invited' as const,
        onboardingCompleted: false,
      };

      const employeeId = await employeesService.createEmployeeWithAuth(
        employeeData,
        user.id
      );

      try {
        await sendPasswordResetEmail(auth, formData.email.trim());
        toast.success('تم إنشاء الموظف وإرسال رابط تهيئة كلمة المرور.');
      } catch {
        toast.success('تم إنشاء الموظف بنجاح.');
        toast.error('تعذر إرسال رابط تهيئة كلمة المرور تلقائيًا.');
      }

      onSuccess({
        id: employeeId,
        ...employeeData,
        isActive: true,
        accountStatus: 'invited',
        onboardingCompleted: false,
        createdBy: user.id,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      } as Employee);
      onClose();
    } catch (error) {
      setErrors({ general: friendlyError(error) });
      toast.error('تعذر إنشاء الموظف.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        if (!isSubmitting) onClose();
      }}
      title='إضافة موظف'
      description='أنشئ حساب موظف مرتبطًا بالمدرس/المؤسسة مع صلاحياته الأساسية.'
      size='xl'
      footer={
        <>
          <Button variant='secondary' onClick={onClose} disabled={isSubmitting}>
            إلغاء
          </Button>
          <Button
            type='submit'
            form='create-employee-form'
            loading={isSubmitting}
            leadingIcon={<Save className='h-4 w-4' />}
          >
            حفظ الموظف
          </Button>
        </>
      }
    >
      <form id='create-employee-form' className='space-y-6' onSubmit={handleSubmit}>
        {errors.general ? (
          <div className='rounded-lg border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700 dark:border-danger-900 dark:bg-danger-950 dark:text-danger-200'>
            {errors.general}
          </div>
        ) : null}

        <section className='space-y-4'>
          <div className='flex items-center gap-2 text-sm font-bold text-secondary-900 dark:text-white'>
            <UserPlus className='h-4 w-4 text-primary-600' />
            البيانات الأساسية
          </div>
          <div className='grid gap-4 md:grid-cols-2'>
            <Input
              label='اسم الموظف'
              value={formData.name}
              onChange={event => setField('name', event.target.value)}
              error={errors.name}
              required
            />
            <Input
              label='البريد الإلكتروني'
              type='email'
              value={formData.email}
              onChange={event => setField('email', event.target.value)}
              error={errors.email}
              required
            />
            <Input
              label='الهاتف'
              type='tel'
              value={formData.phone}
              onChange={event => setField('phone', event.target.value)}
              error={errors.phone}
            />
            <Input
              label='رقم الموظف'
              value={formData.employeeId}
              onChange={event => setField('employeeId', event.target.value)}
              error={errors.employeeId}
              required
            />
          </div>
        </section>

        <section className='space-y-4'>
          <div className='flex items-center gap-2 text-sm font-bold text-secondary-900 dark:text-white'>
            <ShieldCheck className='h-4 w-4 text-primary-600' />
            الوظيفة والصلاحيات
          </div>
          <div className='grid gap-4 md:grid-cols-2'>
            <SelectField
              label='القسم'
              value={formData.department}
              onChange={value =>
                setField('department', value as Employee['department'])
              }
              options={departments}
            />
            <SelectField
              label='الدور'
              value={formData.role}
              onChange={value => {
                const nextRole = value as Employee['role'];
                const selectedRole = roles.find(role => role.value === nextRole);
                setFormData(current => ({
                  ...current,
                  role: nextRole,
                  position: selectedRole?.position || current.position,
                }));
              }}
              options={roles}
            />
            <Input
              label='المسمى الوظيفي'
              value={formData.position}
              onChange={event => setField('position', event.target.value)}
              error={errors.position}
              required
            />
            <SelectField
              label='نوع العقد'
              value={formData.contractType}
              onChange={value => setField('contractType', value as ContractType)}
              options={[
                { value: 'full_time', label: 'دوام كامل' },
                { value: 'part_time', label: 'دوام جزئي' },
                { value: 'contract', label: 'تعاقد' },
                { value: 'intern', label: 'تدريب' },
              ]}
            />
            <Input
              label='تاريخ التعيين'
              type='date'
              value={formData.hireDate}
              onChange={event => setField('hireDate', event.target.value)}
              error={errors.hireDate}
              required
            />
            <Input
              label='الرقم القومي'
              value={formData.nationalId}
              onChange={event => setField('nationalId', event.target.value)}
            />
          </div>
          <div className='flex flex-wrap gap-2 rounded-lg bg-secondary-50 p-3 dark:bg-secondary-800'>
            <StatusBadge tone={permissions.canAccessHR ? 'success' : 'neutral'}>
              {permissions.canAccessHR ? 'له وصول للموارد البشرية' : 'بدون وصول HR'}
            </StatusBadge>
            <StatusBadge tone={permissions.canCreate ? 'success' : 'neutral'}>
              {permissions.canCreate ? 'إنشاء وتعديل' : 'قراءة فقط'}
            </StatusBadge>
            <StatusBadge tone={permissions.canViewFinancials ? 'success' : 'neutral'}>
              {permissions.canViewFinancials ? 'صلاحيات مالية' : 'بدون صلاحيات مالية'}
            </StatusBadge>
          </div>
        </section>

        <section className='space-y-4'>
          <div className='flex items-center gap-2 text-sm font-bold text-secondary-900 dark:text-white'>
            <Mail className='h-4 w-4 text-primary-600' />
            الراتب والبيانات الشخصية
          </div>
          <div className='grid gap-4 md:grid-cols-2'>
            <Input
              label='الراتب الأساسي'
              type='number'
              min='0'
              value={formData.baseSalary}
              onChange={event => setField('baseSalary', event.target.value)}
              error={errors.baseSalary}
              hint='العملة المعتمدة: EGP'
              required
            />
            <SelectField
              label='نوع الراتب'
              value={formData.payrollType}
              onChange={value => setField('payrollType', value as PayrollType)}
              options={[
                { value: 'monthly', label: 'شهري' },
                { value: 'hourly', label: 'بالساعة' },
                { value: 'daily', label: 'يومي' },
              ]}
            />
            <Input
              label='تاريخ الميلاد'
              type='date'
              value={formData.dateOfBirth}
              onChange={event => setField('dateOfBirth', event.target.value)}
            />
            <Input
              label='العنوان'
              value={formData.address}
              onChange={event => setField('address', event.target.value)}
            />
            <Input
              label='اسم جهة الاتصال الطارئة'
              value={formData.emergencyContactName}
              onChange={event =>
                setField('emergencyContactName', event.target.value)
              }
            />
            <Input
              label='هاتف جهة الاتصال الطارئة'
              type='tel'
              value={formData.emergencyContactPhone}
              onChange={event =>
                setField('emergencyContactPhone', event.target.value)
              }
              error={errors.emergencyContactPhone}
            />
            <Input
              label='صلة القرابة'
              value={formData.emergencyContactRelation}
              onChange={event =>
                setField('emergencyContactRelation', event.target.value)
              }
            />
          </div>
        </section>
      </form>
    </Modal>
  );
};

const SelectField: React.FC<{
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
}> = ({ label, value, options, onChange }) => (
  <label className='space-y-1.5 text-sm font-semibold text-secondary-700 dark:text-secondary-200'>
    <span>{label}</span>
    <select
      value={value}
      onChange={event => onChange(event.target.value)}
      className='min-h-11 w-full rounded-lg border border-secondary-300 bg-white px-3 text-sm text-secondary-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 dark:border-secondary-700 dark:bg-secondary-900 dark:text-white'
    >
      {options.map(option => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </label>
);

export default CreateEmployeeModal;
